import React, { useState, useEffect, useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Checkbox,
  ListItemText,
  OutlinedInput,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  alpha,
  Paper,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import SecurityIcon from '@mui/icons-material/Security'
import {
  getAllowedEventTypes,
  canManageMonitoring,
  getMonitoringRoleType,
  updatePatientMonitoring,
  enablePatientMonitoring,
  disablePatientMonitoring,
  MONITORING_SCOPE_OPTIONS,
  MONITORING_POLICY_OPTIONS,
  EVENT_MODE_OPTIONS,
  MONITORING_ACTION_OPTIONS,
} from 'src/supportPortal/services/patientMonitoringService'
import { toast } from 'react-toastify'

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(2),
    maxWidth: 560,
    width: '100%',
  },
}))

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  fontWeight: 600,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: theme.spacing(1.5),
  marginTop: theme.spacing(2),
}))

const CompactSelect = styled(FormControl)(({ theme }) => ({
  '& .MuiInputLabel-root': {
    fontSize: '13px',
  },
  '& .MuiOutlinedInput-root': {
    fontSize: '13px',
  },
}))

const InfoBox = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  backgroundColor: alpha('#6779e3', 0.05),
  border: '1px solid',
  borderColor: alpha('#6779e3', 0.2),
  borderRadius: theme.spacing(1),
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
}))

const DEFAULT_MONITORING_ACTIONS = [
  'JOIN',
  'LEAVE',
  'JOIN_AGORA_CALL',
  'LEAVE_AGORA_CALL',
  'AUTO_CONNECT_PAUSED',
  'AUTO_CONNECT_RESUMED',
  'DISCONNECTED',
]

export default function PatientMonitoringDialog({
  open,
  onClose,
  patient,
  onSuccess,
}) {
  const [loading, setLoading] = useState(false)
  const [monitored, setMonitored] = useState(false)
  const [scope, setScope] = useState('PATIENT_ONLY')
  const [policy, setPolicy] = useState('FIRST_PER_ACTION')
  const [eventModes, setEventModes] = useState(['aumhum'])
  const [eventTypes, setEventTypes] = useState([])
  const [actions, setActions] = useState(DEFAULT_MONITORING_ACTIONS)

  // Get role-specific allowed event types
  const allowedEventTypes = useMemo(() => getAllowedEventTypes(), [])
  const roleType = useMemo(() => getMonitoringRoleType(), [])
  const hasPermission = useMemo(() => canManageMonitoring(), [])

  // Initialize state from patient data
  useEffect(() => {
    if (patient) {
      setMonitored(patient.monitored || false)

      const settings = patient.monitoringSettings || {}
      setScope(settings.scope || 'PATIENT_ONLY')
      setPolicy(settings.policy || 'FIRST_PER_ACTION')
      setEventModes(settings.eventModes?.length ? settings.eventModes : ['aumhum'])
      setActions(settings.actions?.length ? settings.actions : DEFAULT_MONITORING_ACTIONS)

      // Filter event types to only include allowed ones for this role
      const currentEventTypes = settings.eventTypes || []
      const allowedValues = allowedEventTypes.map((et) => et.value)
      const filteredEventTypes = currentEventTypes.filter((et) => allowedValues.includes(et))

      // If patient is monitored but has no valid event types for this role, use all allowed
      if (patient.monitored && filteredEventTypes.length === 0) {
        setEventTypes(allowedValues)
      } else {
        setEventTypes(filteredEventTypes.length > 0 ? filteredEventTypes : allowedValues)
      }
    }
  }, [patient, allowedEventTypes])

  // Handle save
  const handleSave = async () => {
    if (!patient?.id) return

    setLoading(true)
    try {
      const result = await updatePatientMonitoring({
        patientId: patient.id,
        monitored,
        scope,
        policy,
        eventModes,
        eventTypes,
        actions,
      })

      if (result.success) {
        toast.success(monitored ? 'Monitoring enabled successfully' : 'Monitoring settings updated')
        onSuccess?.(result.patientInfo)
        onClose()
      } else {
        toast.error(result.message || 'Failed to update monitoring')
      }
    } catch (error) {
      console.error('Error updating monitoring:', error)
      toast.error('Failed to update monitoring settings')
    } finally {
      setLoading(false)
    }
  }

  // Quick enable with defaults
  const handleQuickEnable = async () => {
    if (!patient?.id) {
      toast.error('No patient selected')
      return
    }

    setLoading(true)
    try {
      const result = await enablePatientMonitoring(patient.id)
      if (result.success) {
        toast.success('Monitoring enabled with default settings')
        onSuccess?.(result.patientInfo)
        onClose()
      } else {
        toast.error(result.message || 'Failed to enable monitoring')
      }
    } catch (error) {
      toast.error('Failed to enable monitoring')
    } finally {
      setLoading(false)
    }
  }

  // Quick disable
  const handleQuickDisable = async () => {
    if (!patient?.id) return

    setLoading(true)
    try {
      const result = await disablePatientMonitoring(patient.id)
      if (result.success) {
        toast.success('Monitoring disabled')
        onSuccess?.(result.patientInfo)
        onClose()
      } else {
        toast.error(result.message || 'Failed to disable monitoring')
      }
    } catch (error) {
      console.error('Error disabling monitoring:', error)
      toast.error('Failed to disable monitoring')
    } finally {
      setLoading(false)
    }
  }

  if (!hasPermission) {
    return (
      <StyledDialog open={open} onClose={onClose}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MonitorHeartIcon sx={{ color: '#10b981' }} />
              <Typography variant="h6" fontWeight={600}>
                Patient Monitoring
              </Typography>
            </Box>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            You don&apos;t have permission to manage patient monitoring. This feature requires L2/L3 Therapy Support or Clinic Success roles.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </StyledDialog>
    )
  }

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                backgroundColor: monitored ? 'rgba(16, 185, 129, 0.1)' : 'rgba(100, 116, 139, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <MonitorHeartIcon sx={{ color: monitored ? '#10b981' : '#64748b', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Patient Monitoring
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {patient?.name || 'Unknown Patient'}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {/* Role Info */}
        <InfoBox sx={{ mb: 2 }}>
          <SecurityIcon sx={{ fontSize: 18, color: '#6779e3', mt: 0.25 }} />
          <Box>
            <Typography variant="caption" fontWeight={600} color="primary">
              Your Role: {roleType}
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary">
              {`You can configure monitoring for: ${allowedEventTypes.map((e) => e.label).join(', ')}`}
            </Typography>
            {allowedEventTypes.some((eventType) => eventType.value === 'consultation') && (
              <Typography variant="caption" display="block" color="text.secondary">
                Consultation monitoring is available only when your clinic-success access is valid for the patient.
              </Typography>
            )}
          </Box>
        </InfoBox>

        {/* Main Toggle */}
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            backgroundColor: monitored ? 'rgba(16, 185, 129, 0.05)' : 'rgba(100, 116, 139, 0.05)',
            border: '1px solid',
            borderColor: monitored ? 'rgba(16, 185, 129, 0.2)' : 'rgba(100, 116, 139, 0.2)',
            mb: 2,
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={monitored}
                onChange={(e) => setMonitored(e.target.checked)}
                color="success"
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight={600}>
                  {monitored ? 'Monitoring Enabled' : 'Monitoring Disabled'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {monitored
                    ? 'Support agents will receive notifications for patient activity'
                    : 'Enable to track patient activity during booked events'}
                </Typography>
              </Box>
            }
            sx={{ m: 0 }}
          />
        </Box>

        {/* Settings (only show when monitored is enabled) */}
        {monitored && (
          <>
            <Divider sx={{ my: 2 }} />

            {/* Event Types */}
            <SectionTitle>Event Types to Monitor</SectionTitle>
            <CompactSelect size="small" fullWidth>
              <InputLabel>Event Types</InputLabel>
              <Select
                multiple
                value={eventTypes}
                onChange={(e) => setEventTypes(e.target.value)}
                input={<OutlinedInput label="Event Types" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={allowedEventTypes.find((e) => e.value === value)?.label || value}
                        size="small"
                        sx={{ height: 22, fontSize: '11px' }}
                      />
                    ))}
                  </Box>
                )}
              >
                {allowedEventTypes.map((option) => (
                  <MenuItem key={option.value} value={option.value} dense>
                    <Checkbox checked={eventTypes.includes(option.value)} size="small" />
                    <ListItemText primary={option.label} primaryTypographyProps={{ fontSize: '13px' }} />
                  </MenuItem>
                ))}
              </Select>
            </CompactSelect>

            {/* Scope & Policy */}
            <SectionTitle>Scope & Policy</SectionTitle>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <CompactSelect size="small" fullWidth>
                <InputLabel>Scope</InputLabel>
                <Select value={scope} label="Scope" onChange={(e) => setScope(e.target.value)}>
                  {MONITORING_SCOPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </CompactSelect>

              <CompactSelect size="small" fullWidth>
                <InputLabel>Policy</InputLabel>
                <Select value={policy} label="Policy" onChange={(e) => setPolicy(e.target.value)}>
                  {MONITORING_POLICY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </CompactSelect>
            </Box>

            {/* Event Modes */}
            <SectionTitle>Event Modes</SectionTitle>
            <CompactSelect size="small" fullWidth>
              <InputLabel>Event Modes</InputLabel>
              <Select
                multiple
                value={eventModes}
                onChange={(e) => setEventModes(e.target.value)}
                input={<OutlinedInput label="Event Modes" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={EVENT_MODE_OPTIONS.find((e) => e.value === value)?.label || value}
                        size="small"
                        sx={{ height: 22, fontSize: '11px' }}
                      />
                    ))}
                  </Box>
                )}
              >
                {EVENT_MODE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value} dense>
                    <Checkbox checked={eventModes.includes(option.value)} size="small" />
                    <ListItemText primary={option.label} primaryTypographyProps={{ fontSize: '13px' }} />
                  </MenuItem>
                ))}
              </Select>
            </CompactSelect>

            {/* Actions */}
            <SectionTitle>Actions to Monitor</SectionTitle>
            <CompactSelect size="small" fullWidth>
              <InputLabel>Actions</InputLabel>
              <Select
                multiple
                value={actions}
                onChange={(e) => setActions(e.target.value)}
                input={<OutlinedInput label="Actions" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={MONITORING_ACTION_OPTIONS.find((e) => e.value === value)?.label || value}
                        size="small"
                        sx={{ height: 22, fontSize: '11px' }}
                      />
                    ))}
                  </Box>
                )}
              >
                {MONITORING_ACTION_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value} dense>
                    <Checkbox checked={actions.includes(option.value)} size="small" />
                    <ListItemText primary={option.label} primaryTypographyProps={{ fontSize: '13px' }} />
                  </MenuItem>
                ))}
              </Select>
            </CompactSelect>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        {/* Quick action buttons */}
        {!patient?.monitored && !monitored && (
          <Button
            variant="outlined"
            color="success"
            onClick={handleQuickEnable}
            disabled={loading}
            startIcon={<MonitorHeartIcon />}
            sx={{ mr: 'auto' }}
          >
            Quick Enable
          </Button>
        )}
        {patient?.monitored && monitored && (
          <Button
            variant="outlined"
            color="error"
            onClick={handleQuickDisable}
            disabled={loading}
            sx={{ mr: 'auto' }}
          >
            Disable Monitoring
          </Button>
        )}

        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || (monitored && eventTypes.length === 0)}
          startIcon={loading && <CircularProgress size={16} color="inherit" />}
        >
          {loading ? 'Saving...' : 'Save Settings'}
        </Button>
      </DialogActions>
    </StyledDialog>
  )
}
